import { MAX_VOICE_BYTES, MAX_VOICE_TRACKS_PER_DEMO, nudgeVoice, partAt, partStarts, syncVoiceAt, type DemoDetail, type Replay, type VoiceSource, type VoiceTrack } from "@69ly/shared";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import { Icon } from "../components/Icon.tsx";
import { Menu } from "../components/Menu.tsx";
import { SoloPlayer } from "../components/SoloPlayer.tsx";
import type { RoundIndex } from "../demo/scene.ts";
import { useTrackPlayer, type SoloTrack } from "../demo/track-player.ts";
import { audible, audioClock, parseAudioClock, VOICE_MAX_SPEED, type VoiceMix, type VoicePlayback } from "../demo/voice.ts";
import { useQueryClient } from "@tanstack/react-query";
import { ApiFailure } from "../lib/api.ts";
import { matchKeys, uploadVoice, useDeleteVoice, useUpdateVoice, voiceAudioUrl } from "../lib/demo-queries.ts";
import { useAddVoiceSession, useMapVoiceSessions, useRemoveVoiceSession, useUseVoiceFile, useVoiceFiles } from "../lib/discord-queries.ts";
import { errorText } from "../lib/errors.ts";

const AUDIO_ACCEPT = "audio/*,.mp3,.ogg,.opus,.oga,.m4a,.webm,.wav,.flac";

/**
 * The voice tracks of a map: who is heard, how loud, alone or not; and a file to add.
 * The tracks play wherever the viewer is; this panel only mixes them.
 */
export function VoiceTracksPanel({
  demo,
  tracks,
  mix,
  onMix,
  playback,
  selected,
  onSelect,
  speed,
}: {
  demo: DemoDetail;
  tracks: VoiceTrack[];
  mix: VoiceMix;
  onMix: (change: Partial<VoiceMix> | ((m: VoiceMix) => Partial<VoiceMix>)) => void;
  playback: VoicePlayback;
  selected: string | null;
  onSelect: (id: string) => void;
  speed: number;
}) {
  const { t } = useTranslation();
  // One voice source per map (0.24.0): the bot's recording, or files people uploaded.
  const sessionId = tracks.find((x) => x.sessionId)?.sessionId ?? null;
  const source: VoiceSource | null = tracks.length === 0 ? null : sessionId ? "bot" : "files";
  const [changing, setChanging] = useState(false);
  const room = tracks.length < MAX_VOICE_TRACKS_PER_DEMO;
  const replacing = source ? { kind: source, count: tracks.length } : null;
  return (
    <div className="voice-panel">
      {source && <SourceHeader demo={demo} tracks={tracks} sessionId={sessionId} changing={changing} onChange={() => setChanging((c) => !c)} />}
      {/* Changing the source: the other choices right under the header, where the button is. */}
      {source && changing && demo.canAddVoice && (
        <div className="voice-replace">
          <FromDiscord demoId={demo.id} empty={false} sessionId={sessionId} replacing={replacing} onDone={() => setChanging(false)} />
          {source === "bot" && room && <FromTeamFiles demo={demo} replacing={tracks.length} onDone={() => setChanging(false)} />}
          {source === "bot" && room && (
            <AddVoice
              demoId={demo.id}
              onAdded={(id) => {
                setChanging(false);
                onSelect(id);
              }}
              first={false}
              replacing={tracks.length}
            />
          )}
        </div>
      )}
      {tracks.length === 0 ? (
        <div className="voice-empty">
          <span className="voice-empty-icon" aria-hidden="true">
            <Icon name="mic" size={22} />
          </span>
          <h3>{t("demos.voice.emptyTitle")}</h3>
          <p className="muted small">{t("demos.voice.emptyHelp")}</p>
        </div>
      ) : (
        <>
          <label className="switch-row voice-master">
            <span>
              <b>{t("demos.voice.on")}</b>
              <span className="muted small">{speed > VOICE_MAX_SPEED ? t("demos.voice.tooFast", { max: VOICE_MAX_SPEED }) : t("demos.voice.onHelp")}</span>
            </span>
            <input type="checkbox" role="switch" className="switch" checked={mix.on} onChange={(e) => onMix({ on: e.target.checked })} />
          </label>
          <ul className="voice-tracks" aria-label={t("demos.voice.tracks")}>
            {tracks.map((track) => {
              const at = playback.at(track);
              const length = playback.durations[track.id];
              const where = at < 0 ? t("demos.voice.notYet", { in: audioClock(-at) }) : length !== undefined && at >= length ? t("demos.voice.ended") : audioClock(at);
              const heard = audible(mix, track.id);
              return (
                <li key={track.id} className={`voice-track${selected === track.id ? " is-selected" : ""}${heard ? "" : " is-silent"}`}>
                  <button type="button" className="voice-track-name" aria-pressed={selected === track.id} onClick={() => onSelect(track.id)} title={t("demos.voice.lineUpThis")}>
                    <b>{track.label}</b>
                    <span className="muted small mono">{where}</span>
                  </button>
                  <div className="voice-track-mix">
                    <button
                      type="button"
                      className="btn btn-quiet btn-icon btn-small"
                      aria-pressed={!!mix.muted[track.id]}
                      aria-label={t(mix.muted[track.id] ? "demos.voice.unmute" : "demos.voice.mute", { name: track.label })}
                      title={t(mix.muted[track.id] ? "demos.voice.unmute" : "demos.voice.mute", { name: track.label })}
                      onClick={() => onMix((m) => ({ muted: { ...m.muted, [track.id]: !m.muted[track.id] } }))}
                    >
                      <Icon name={mix.muted[track.id] ? "muted" : "volume"} />
                    </button>
                    <input
                      type="range"
                      className="voice-volume"
                      min={0}
                      max={1}
                      step={0.05}
                      value={mix.volume[track.id] ?? 1}
                      aria-label={t("demos.voice.volume", { name: track.label })}
                      onChange={(e) => onMix((m) => ({ volume: { ...m.volume, [track.id]: Number(e.target.value) } }))}
                    />
                    <button
                      type="button"
                      className="btn btn-quiet btn-icon btn-small"
                      aria-pressed={mix.solo === track.id}
                      aria-label={t("demos.voice.solo", { name: track.label })}
                      title={t("demos.voice.solo", { name: track.label })}
                      onClick={() => onMix((m) => ({ solo: m.solo === track.id ? null : track.id }))}
                    >
                      <Icon name="headphones" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <VoiceDebug playback={playback} />
        </>
      )}
      {demo.canAddVoice && source === null && <FromDiscord demoId={demo.id} empty sessionId={null} replacing={null} onDone={() => undefined} />}
      {demo.canAddVoice && source === "bot" && <MissingTracks demoId={demo.id} sessionId={sessionId!} />}
      {/* More files beside files; files instead of a recording only through "Change…". */}
      {demo.canAddVoice && room && (source === null || source === "files") && <AddVoice demoId={demo.id} onAdded={onSelect} first={tracks.length === 0} replacing={0} />}
      {demo.canAddVoice && room && (source === null || source === "files") && <FromTeamFiles demo={demo} replacing={0} onDone={() => undefined} />}
      {tracks.length === 0 && !demo.canAddVoice && <p className="muted small">{t("demos.voice.cannotAdd")}</p>}
    </div>
  );
}

/**
 * `?debug=voice`: how each track is played and how often it was put back in
 * step, to check on a phone that it plays smoothly (not translated: a tool for us).
 */
function VoiceDebug({ playback }: { playback: VoicePlayback }) {
  const on = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "voice";
  const [, redraw] = useState(0);
  useEffect(() => {
    if (!on) return;
    const timer = window.setInterval(() => redraw((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [on]);
  if (!on) return null;
  return (
    <pre className="voice-debug" data-testid="voice-debug">
      {playback
        .stats()
        .map(
          (s) =>
            `${s.id.slice(0, 6)} ${s.kind} restarts=${s.restarts} nudges=${s.nudges} decoded=${s.decoded} (${s.decodeMs} ms) fetched=${Math.round(s.fetched / 1024)} KB${s.error ? ` error=${s.error}` : ""}`,
        )
        .join("\n")}
    </pre>
  );
}

/** Where the map's voice comes from, and the way to take it off or swap it (0.24.0). */
function SourceHeader({ demo, tracks, sessionId, changing, onChange }: { demo: DemoDetail; tracks: VoiceTrack[]; sessionId: string | null; changing: boolean; onChange: () => void }) {
  const { t, i18n } = useTranslation();
  const sessions = useMapVoiceSessions(demo.id, true);
  const remove = useRemoveVoiceSession(demo.id);
  const [asking, setAsking] = useState(false);
  const canEdit = tracks.every((x) => x.canEdit);
  const when = (iso: string) => new Date(iso).toLocaleString(i18n.language, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  if (sessionId) {
    const session = sessions.data?.find((x) => x.id === sessionId);
    const minutes = session?.endedAt ? Math.max(1, Math.round((Date.parse(session.endedAt) - Date.parse(session.startedAt)) / 60_000)) : null;
    return (
      <div className="voice-source">
        <span className="voice-source-icon" aria-hidden="true">
          <Icon name="mic" size={18} />
        </span>
        <div className="voice-source-text">
          <b>{session ? t("demos.voice.sourceBot", { when: when(session.startedAt), count: tracks.length }) : t("demos.voice.sourceBotShort", { count: tracks.length })}</b>
          <span className="muted small">
            {session && `${session.channelName}${minutes ? ` · ${t("recordings.minutesShort", { m: minutes })}` : ""} · `}
            <Link to={`/matches?view=recordings&rec=${sessionId}`}>{t("demos.voice.openRecording")}</Link>
          </span>
        </div>
        {demo.canAddVoice && (
          <div className="voice-source-actions">
            {canEdit && (
              <button type="button" className="btn btn-quiet btn-small" onClick={() => setAsking(true)}>
                {t("demos.voice.removeRecording")}
              </button>
            )}
            <button type="button" className="btn btn-quiet btn-small" aria-expanded={changing} onClick={onChange}>
              <Icon name="swap" size={14} />
              {changing ? t("common.cancel") : t("demos.voice.change")}
            </button>
          </div>
        )}
        {asking && (
          <ConfirmDialog
            title={t("demos.voice.removeTitle")}
            confirm={t("demos.voice.removeConfirm")}
            cancel={t("common.cancel")}
            danger
            busy={remove.isPending}
            onConfirm={() => remove.mutate(sessionId, { onSuccess: () => setAsking(false) })}
            onCancel={() => {
              remove.reset();
              setAsking(false);
            }}
          >
            <p className="muted">{t("demos.voice.removeHelp", { count: tracks.length })}</p>
            {remove.error && (
              <p className="field-error" role="alert">
                {errorText(t, remove.error)}
              </p>
            )}
          </ConfirmDialog>
        )}
      </div>
    );
  }
  const by = tracks[0]?.uploadedBy?.name;
  const date = tracks[0] ? new Date(tracks[0].createdAt).toLocaleDateString(i18n.language, { day: "numeric", month: "short" }) : "";
  const recordings = (sessions.data ?? []).length > 0;
  return (
    <div className="voice-source">
      <span className="voice-source-icon" aria-hidden="true">
        <Icon name="upload" size={18} />
      </span>
      <div className="voice-source-text">
        <b>{t("demos.voice.sourceFiles", { count: tracks.length })}</b>
        <span className="muted small">{by ? t("demos.voice.filesBy", { name: by, date }) : t("demos.voice.filesHelp")}</span>
      </div>
      {demo.canAddVoice && recordings && (
        <div className="voice-source-actions">
          <button type="button" className="btn btn-quiet btn-small" aria-expanded={changing} onClick={onChange}>
            <Icon name="swap" size={14} />
            {changing ? t("common.cancel") : t("demos.voice.useRecording")}
          </button>
        </div>
      )}
    </div>
  );
}

/** The recording on the map is missing some players' tracks (added before they were uploaded): add them. */
function MissingTracks({ demoId, sessionId }: { demoId: string; sessionId: string }) {
  const { t } = useTranslation();
  const sessions = useMapVoiceSessions(demoId, true);
  const add = useAddVoiceSession(demoId);
  const s = sessions.data?.find((x) => x.id === sessionId);
  if (!s || s.added >= s.tracks.length) return null;
  return (
    <div className="voice-missing">
      <button type="button" className="btn btn-secondary btn-small" disabled={add.isPending} onClick={() => add.mutate({ sessionId })}>
        {t("demos.voice.addRecording", { count: s.tracks.length - s.added })}
      </button>
      {add.error && <p className="field-error">{errorText(t, add.error)}</p>}
    </div>
  );
}

/**
 * The team's Discord recordings made around this match: each added in one click, one
 * track per player. With another source on the map, adding one replaces it, after a yes.
 */
function FromDiscord({
  demoId,
  empty,
  sessionId,
  replacing,
  onDone,
}: {
  demoId: string;
  empty: boolean;
  sessionId: string | null;
  replacing: { kind: VoiceSource; count: number } | null;
  onDone: () => void;
}) {
  const { t, i18n } = useTranslation();
  const sessions = useMapVoiceSessions(demoId, true);
  const add = useAddVoiceSession(demoId);
  const [asking, setAsking] = useState<{ id: string; message: string } | null>(null);
  const list = (sessions.data ?? []).filter((x) => x.id !== sessionId);
  const use = (id: string, replace: boolean) =>
    add.mutate(
      { sessionId: id, replace },
      {
        onSuccess: () => {
          setAsking(null);
          onDone();
        },
        onError: (e) => {
          // Someone added the other kind meanwhile: ask, then replace.
          if (e instanceof ApiFailure && e.code === "other_source") setAsking({ id, message: e.message });
        },
      },
    );
  if (!list.length) {
    return empty && sessions.isSuccess ? (
      <p className="muted small voice-discord-hint">
        {t("demos.voice.discordHint")} <Link to="/settings/team#discord">{t("demos.voice.discordSetUp")}</Link>
      </p>
    ) : replacing && sessions.isSuccess ? (
      <p className="muted small voice-discord-hint">{t("demos.voice.noOtherRecording")}</p>
    ) : null;
  }
  const question = replacing ? t(replacing.kind === "bot" ? "demos.voice.replaceBotHelp" : "demos.voice.replaceFilesHelp", { count: replacing.count }) : null;
  return (
    <section className="voice-discord" aria-labelledby={`discord-${demoId}`}>
      <h3 id={`discord-${demoId}`} className="eyebrow">
        {replacing ? t(replacing.kind === "bot" ? "demos.voice.otherRecording" : "demos.voice.replaceWithRecording") : t("demos.voice.fromDiscord")}
      </h3>
      <ul>
        {list.map((s) => (
          <li key={s.id}>
            <span>
              <b>{s.channelName}</b>
              <span className="muted small">
                {new Date(s.startedAt).toLocaleString(i18n.language, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} ·{" "}
                {s.tracks.map((x) => x.name).join(", ")}
              </span>
            </span>
            <button type="button" className="btn btn-secondary btn-small" disabled={add.isPending} onClick={() => (question ? setAsking({ id: s.id, message: question }) : use(s.id, false))}>
              {replacing ? t("demos.voice.useThis") : t("demos.voice.addRecording", { count: s.tracks.length })}
            </button>
          </li>
        ))}
      </ul>
      {add.error && !(add.error instanceof ApiFailure && add.error.code === "other_source") && <p className="field-error">{errorText(t, add.error)}</p>}
      {add.isSuccess && <p className="muted small">{t("demos.voice.addedHelp")}</p>}
      {asking && (
        <ConfirmDialog
          eyebrow={t("recordings.oneSource")}
          title={t(replacing?.kind === "bot" ? "demos.voice.swapTitle" : "demos.voice.replaceFilesTitle")}
          confirm={t("demos.voice.replaceGo")}
          cancel={t("demos.voice.keep")}
          busy={add.isPending}
          onConfirm={() => use(asking.id, true)}
          onCancel={() => setAsking(null)}
        >
          <p className="muted">{asking.message}</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

/**
 * The team's uploaded files that no map plays (0.25.0): taken off a map, they wait
 * in Recordings and come back from here. Over a bot recording, it replaces it after a yes.
 */
function FromTeamFiles({ demo, replacing, onDone }: { demo: DemoDetail; replacing: number; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const files = useVoiceFiles(demo.match.teamId);
  const use = useUseVoiceFile(demo.id);
  const [asking, setAsking] = useState<string | null>(null);
  const free = (files.data ?? []).filter((f) => f.usedOn.length === 0);
  if (!free.length) return null;
  const put = (fileId: string, replace: boolean) =>
    use.mutate(
      { fileId, replace },
      {
        onSuccess: () => {
          setAsking(null);
          onDone();
        },
        onError: (e) => {
          if (e instanceof ApiFailure && e.code === "other_source") setAsking(fileId);
        },
      },
    );
  return (
    <section className="voice-discord voice-team-files" aria-labelledby={`files-${demo.id}`}>
      <h3 id={`files-${demo.id}`} className="eyebrow">
        {t("demos.voice.teamFiles")}
      </h3>
      <ul>
        {free.map((f) => (
          <li key={f.id}>
            <span>
              <b>{f.label}</b>
              <span className="muted small">
                {[
                  f.uploadedBy?.name,
                  new Date(f.createdAt).toLocaleDateString(i18n.language, { day: "numeric", month: "short" }),
                  f.durationSeconds ? t("recordings.minutesShort", { m: Math.max(1, Math.round(f.durationSeconds / 60)) }) : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
            <button type="button" className="btn btn-secondary btn-small" disabled={use.isPending} onClick={() => (replacing ? setAsking(f.id) : put(f.id, false))}>
              {t("demos.voice.useThis")}
            </button>
          </li>
        ))}
      </ul>
      <p className="muted small">{t("demos.voice.teamFilesHelp")}</p>
      {use.error && !(use.error instanceof ApiFailure && use.error.code === "other_source") && <p className="field-error">{errorText(t, use.error)}</p>}
      {asking && (
        <ConfirmDialog
          eyebrow={t("recordings.oneSource")}
          title={t("demos.voice.replaceBotTitle")}
          confirm={t("demos.voice.replaceWithFile")}
          cancel={t("demos.voice.keepRecording")}
          busy={use.isPending}
          onConfirm={() => put(asking, true)}
          onCancel={() => setAsking(null)}
        >
          <p className="muted">{t("demos.voice.replaceBotHelp", { count: replacing || 1 })}</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

/** A file picked, named, sent with its progress. */
function AddVoice({ demoId, onAdded, first, replacing }: { demoId: string; onAdded: (id: string) => void; first: boolean; replacing: number }) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const labelId = useId();

  const pick = (f: File | undefined) => {
    setError(null);
    if (!f) return;
    if (f.size > MAX_VOICE_BYTES) return setError(t("demos.voice.tooBig", { mb: MAX_VOICE_BYTES / 1024 / 1024 }));
    setFile(f);
    setLabel(f.name.replace(/\.[^.]+$/, "").slice(0, 60));
  };
  const send = async (e?: FormEvent, confirmed = false) => {
    e?.preventDefault();
    if (!file) return;
    // A bot recording is on the map: the file replaces it, once the player said yes.
    if (replacing && !confirmed) return setAsking(true);
    setAsking(false);
    setError(null);
    setProgress(0);
    try {
      const track = await uploadVoice(demoId, file, label.trim() || file.name, setProgress, replacing > 0);
      await qc.invalidateQueries({ queryKey: matchKeys.voices(demoId) });
      void qc.invalidateQueries({ queryKey: ["voice-files"] });
      setFile(null);
      if (input.current) input.current.value = "";
      onAdded(track.id);
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setProgress(null);
    }
  };

  return (
    <form className="voice-add" onSubmit={(e) => void send(e)}>
      {replacing > 0 && <h3 className="eyebrow">{t("demos.voice.replaceWithFiles")}</h3>}
      <input ref={input} type="file" accept={AUDIO_ACCEPT} className="visually-hidden" tabIndex={-1} aria-hidden="true" onChange={(e) => pick(e.target.files?.[0])} />
      {!file ? (
        <button type="button" className={`btn ${first ? "btn-primary" : "btn-secondary"} btn-small`} onClick={() => input.current?.click()}>
          <Icon name="upload" />
          {t("demos.voice.add")}
        </button>
      ) : (
        <>
          <p className="small voice-file">
            <Icon name="mic" />
            <span>{file.name}</span>
            <span className="muted mono">{(file.size / 1e6).toFixed(1)} MB</span>
          </p>
          <label className="field" htmlFor={labelId}>
            <span>{t("demos.voice.label")}</span>
            <input id={labelId} value={label} maxLength={60} placeholder={t("demos.voice.labelPlaceholder")} onChange={(e) => setLabel(e.target.value)} disabled={progress !== null} />
          </label>
          {progress !== null && <progress className="clip-progress" value={progress} max={1} aria-label={t("demos.voice.sending", { percent: Math.round(progress * 100) })} />}
          <div className="row">
            <button type="submit" className="btn btn-primary btn-small" disabled={progress !== null}>
              {progress !== null ? t("demos.voice.sending", { percent: Math.round(progress * 100) }) : t("demos.voice.send")}
            </button>
            <button type="button" className="btn btn-quiet btn-small" disabled={progress !== null} onClick={() => setFile(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </>
      )}
      {first && !file && <p className="muted small">{t("demos.voice.formats", { mb: MAX_VOICE_BYTES / 1024 / 1024 })}</p>}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {asking && (
        <ConfirmDialog
          eyebrow={t("recordings.oneSource")}
          title={t("demos.voice.replaceBotTitle")}
          confirm={t("demos.voice.replaceWithMine")}
          cancel={t("demos.voice.keepRecording")}
          onConfirm={() => void send(undefined, true)}
          onCancel={() => setAsking(false)}
        >
          <p className="muted">{t("demos.voice.replaceBotHelp", { count: replacing })}</p>
        </ConfirmDialog>
      )}
    </form>
  );
}

const NUDGES = [-10, -1, -0.1, 0.1, 1, 10] as const;

/**
 * Lining a track up with the replay: nudges, a time typed in, or the moment
 * found by ear in the audio itself, matched to the moment on screen.
 */
export function VoiceSyncPanel({
  demo,
  replay,
  round,
  tick,
  track,
  playback,
}: {
  demo: DemoDetail;
  replay: Replay;
  round: RoundIndex;
  tick: number;
  track: VoiceTrack | null;
  playback: VoicePlayback;
}) {
  const { t } = useTranslation();
  if (!track) return <p className="muted small voice-sync-none">{t("demos.voice.pickOne")}</p>;
  return <VoiceSync key={track.id} demo={demo} replay={replay} round={round} tick={tick} track={track} playback={playback} />;
}

function VoiceSync({ demo, replay, round, tick, track, playback }: { demo: DemoDetail; replay: Replay; round: RoundIndex; tick: number; track: VoiceTrack; playback: VoicePlayback }) {
  const { t } = useTranslation();
  const update = useUpdateVoice(demo.id);
  const del = useDeleteVoice(demo.id);
  const [typed, setTyped] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(track.label);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState<"reset" | "delete" | null>(null);
  const starts = partStarts(replay);
  const part = partAt(replay, tick).part;
  const at = playback.at(track);
  const roundSeconds = (tick - round.round.startTick) / replay.tickRate;
  const here = t("demos.notes.at", { n: round.round.number, clock: `${Math.floor(roundSeconds / 60)}:${String(Math.floor(roundSeconds % 60)).padStart(2, "0")}` });
  const fail = (e: unknown) => setError(errorText(t, e));
  const save = (offsets: number[]) => {
    setError(null);
    update.mutate({ id: track.id, offsets }, { onError: fail });
  };
  const typedSeconds = parseAudioClock(typed);

  return (
    <div className="voice-sync">
      <div className="vsync-head">
        <div className="vsync-title">
          <span className="eyebrow">{t("demos.voice.liningUp")}</span>
          {renaming ? (
            <form
              className="vsync-rename"
              onSubmit={(e) => {
                e.preventDefault();
                if (!name.trim()) return;
                update.mutate({ id: track.id, label: name }, { onError: fail, onSuccess: () => setRenaming(false) });
              }}
            >
              <input
                value={name}
                maxLength={60}
                aria-label={t("demos.voice.label")}
                autoFocus
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Escape") return;
                  e.stopPropagation();
                  setName(track.label);
                  setRenaming(false);
                }}
              />
              <button type="submit" className="btn btn-primary btn-small" disabled={!name.trim() || name.trim() === track.label}>
                {t("common.save")}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-small"
                onClick={() => {
                  setName(track.label);
                  setRenaming(false);
                }}
              >
                {t("common.cancel")}
              </button>
            </form>
          ) : (
            <b className="vsync-name">{track.label}</b>
          )}
        </div>
        {track.canEdit && !renaming && (
          <Menu
            label={t("demos.voice.trackOptions", { name: track.label })}
            items={[
              { label: t("demos.voice.renameMenu"), onSelect: () => setRenaming(true) },
              { label: t("demos.voice.resetMenu"), onSelect: () => setAsking("reset") },
              { label: t("demos.voice.deleteMenu"), danger: true, onSelect: () => setAsking("delete") },
            ]}
          />
        )}
      </div>

      <div className="voice-readout" aria-live="polite">
        <span className="eyebrow">{here}</span>
        <b className="mono">{at < 0 ? t("demos.voice.beforeStart") : audioClock(at)}</b>
        <span className="muted small">{t("demos.voice.readout", { name: track.label })}</span>
      </div>
      {starts.length > 1 && <p className="muted small">{t("demos.voice.parts", { n: part + 1, of: starts.length })}</p>}

      {!track.canEdit ? (
        <p className="muted small">{t("demos.voice.readOnly")}</p>
      ) : (
        <>
          <fieldset className="voice-nudge">
            <legend className="eyebrow">{t("demos.voice.nudge")}</legend>
            <div className="voice-nudge-row">
              {NUDGES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="btn btn-secondary btn-small mono"
                  aria-label={t(s < 0 ? "demos.voice.soonerBy" : "demos.voice.laterBy", { s: Math.abs(s) })}
                  onClick={() => save(nudgeVoice(track.offsets, starts, replay.tickRate, tick, s))}
                >
                  {s > 0 ? "+" : "−"}
                  {Math.abs(s)}
                </button>
              ))}
            </div>
            <div className="voice-nudge-ends muted small" aria-hidden="true">
              <span>← {t("demos.voice.sooner")}</span>
              <span>{t("demos.voice.later")} →</span>
            </div>
          </fieldset>

          <ByEar
            demoId={demo.id}
            track={track}
            length={playback.durations[track.id]}
            at={at}
            tick={tick}
            here={here}
            onMatch={(heardAt) => save(syncVoiceAt(track.offsets, starts, replay.tickRate, tick, heardAt))}
          />

          <form
            className="voice-typed"
            onSubmit={(e) => {
              e.preventDefault();
              if (typedSeconds === null) return;
              save(syncVoiceAt(track.offsets, starts, replay.tickRate, tick, typedSeconds));
              setTyped("");
            }}
          >
            <label className="field">
              <span>{t("demos.voice.typed", { at: here })}</span>
              <span className="voice-typed-in">
                <input className="mono" value={typed} inputMode="decimal" placeholder="12:34.5" aria-invalid={typed !== "" && typedSeconds === null} onChange={(e) => setTyped(e.target.value)} />
                <button type="submit" className="btn btn-secondary" disabled={typedSeconds === null}>
                  {t("demos.voice.apply")}
                </button>
              </span>
            </label>
          </form>
        </>
      )}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {asking === "reset" && (
        <ConfirmDialog
          title={t("demos.voice.resetTitle", { name: track.label })}
          confirm={t("demos.voice.resetConfirm")}
          cancel={t("common.cancel")}
          onConfirm={() => {
            setAsking(null);
            save([]);
          }}
          onCancel={() => setAsking(null)}
        >
          <p className="muted">{t("demos.voice.resetHelp")}</p>
        </ConfirmDialog>
      )}
      {asking === "delete" && (
        <ConfirmDialog
          title={t("demos.voice.deleteTitle", { name: track.label })}
          confirm={t("demos.voice.deleteConfirm")}
          cancel={t("common.cancel")}
          danger
          busy={del.isPending}
          onConfirm={() =>
            del.mutate(track.id, {
              onError: (e) => {
                setAsking(null);
                fail(e);
              },
            })
          }
          onCancel={() => setAsking(null)}
        >
          <p className="muted">{t(track.sessionId ? "demos.voice.deleteBotHelp" : "demos.voice.deleteHelp")}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

/**
 * Lining up by ear: our own player (the browser's differs everywhere and cannot play
 * Ogg on some phones), at the voice's moment when the replay moves; then "match".
 */
function ByEar({
  demoId,
  track,
  length: known,
  at,
  tick,
  here,
  onMatch,
}: {
  demoId: string;
  track: VoiceTrack;
  /** The length the replay's player already found, if any. */
  length: number | undefined;
  at: number;
  tick: number;
  here: string;
  onMatch: (heardAt: number) => void;
}) {
  const { t } = useTranslation();
  const player = useTrackPlayer();
  const solo: SoloTrack = { id: track.id, url: voiceAudioUrl(demoId, track.id), contentType: track.contentType, bytes: track.bytes, duration: known };
  const heardAt = player.id === track.id ? player.at : Math.max(0, at);

  // Where the voice is now, each time the replay moves (not while listening).
  useEffect(() => {
    if (!player.playing) player.cue(solo, Math.max(0, at));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, track.id]);

  return (
    <section className="voice-ear" aria-labelledby={`ear-${track.id}`}>
      <h3 id={`ear-${track.id}`} className="eyebrow">
        {t("demos.voice.byEar")}
      </h3>
      <p className="muted small">{t("demos.voice.byEarHelp")}</p>
      <SoloPlayer player={player} track={solo} at={heardAt} testId="voice-heard-at" />
      <button type="button" className="btn btn-primary vmatch" onClick={() => onMatch(heardAt)}>
        <span className="vmatch-main">
          <Icon name="sync" />
          {t("demos.voice.matchVoice")}
        </span>
        <span className="vmatch-sub mono">
          {audioClock(heardAt)} → {here}
        </span>
      </button>
    </section>
  );
}
